import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { cx } from "@nju/css";
import { useOfferModeWithoutFallback } from "@nju/data-access-portal-offers";
import { getIdentityProviderLoginUrl } from "@nju/data-access-portal-user";
import { pushSimpleEvent } from "@nju/data-layer";
import { redirectToOrchestratorState } from "@nju/feature-portal-orchestrator";
import { ErrorMessage, Input } from "@nju/form";
import { Icon } from "@nju/icons";
import { portalRoutes } from "@nju/routes";
import { A, Button, ButtonsWrapper, Separator, Text } from "@nju/ui";
import { getFormSessionStorageOrQuery, getFromQuery } from "@nju/utils";
import { PasswordValidation } from "./PasswordValidations";
import { registrationMachine } from "./registration.machine";
import { mapInputsErrors } from "./utils";

export function RegistrationForm() {
  const router = useRouter();
  const mode = useOfferModeWithoutFallback();
  const [shouldShowPassword, setIsPassVisible] = React.useState(false);
  const params = portalRoutes.zalozKonto.input(router.query).unwrap();

  const { offerId, flow } = React.useMemo(() => {
    return {
      offerId: getFormSessionStorageOrQuery("oferta", router.query),
      flow:
        getFromQuery("flow", router.query) === "multiOrder"
          ? "multiOrder"
          : undefined,
    };
  }, [router.query]);

  const [{ context, tags }, send] = useMachine(registrationMachine, {
    context: {
      offerIds: offerId ? [offerId] : undefined,
      mode,
    },
    actions: {
      onSubmit: (_, event) => {
        if (event.type !== "REGISTRATION_SUCCESSFUL") {
          return;
        }

        if (
          mode === "ftth" &&
          offerId &&
          event.orchestrator?.type === "NJU_FTTH_ONBOARDING"
        ) {
          return redirectToOrchestratorState({
            state: event.orchestrator.state,
            mode: "ftth",
            query: {
              IDENTITY: {
                type: "registration",
              },
            },
          });
        }

        if (mode && ["mobile", "postpaid"].includes(mode)) {
          return router.push(
            portalRoutes.wybierak.get({
              ...(params?.amount && { amount: params?.amount }),
            }),
          );
        }

        return router.push(
          portalRoutes.mojeKonto.index.get({ mode: mode || "ftth" }),
        );
      },
    },
  });

  React.useEffect(() => {
    const errors = mapInputsErrors(context.emailError, context.passwordError);

    if (errors) {
      pushSimpleEvent({
        event: "error_create_account",
        eventName: errors,
      });
    }
  }, [context.emailError, context.passwordError]);

  const isLoading = tags.has("loading");

  const storeDataInSessionStorage = React.useCallback(() => {
    if (offerId) {
      sessionStorage.setItem("oferta", offerId);
      sessionStorage.setItem("flow", "ONBOARDING");
    }
  }, [offerId]);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> =
    React.useCallback(
      (event) => {
        event.preventDefault();
        send({ type: "SUBMIT" });
      },
      [send],
    );

  const handleEmailChange: React.ChangeEventHandler<HTMLInputElement> =
    React.useCallback(
      ({ target }) => {
        send({ type: "SET_INPUT_VALUE", name: "email", value: target.value });
      },
      [send],
    );

  const handlePasswordChange: React.ChangeEventHandler<HTMLInputElement> =
    React.useCallback(
      ({ target }) => {
        send({
          type: "SET_INPUT_VALUE",
          name: "password",
          value: target.value,
        });
      },
      [send],
    );

  return (
    <form
      autoComplete="false"
      className={cx("mt-3 from-tablet:mt-5", {
        "max-w-[600px]": !offerId,
      })}
      onSubmit={handleSubmit}
    >
      <div className="flex flex-col">
        <div className="mb-3 w-full">
          <Input
            autoFocus={true}
            label="podaj e-mail"
            labelBackground="white"
            name="email"
            onChange={handleEmailChange}
            value={context.email}
          />
          {context.emailError && <ErrorMessage message={context.emailError} />}
        </div>
        <div className="grid gap-4 pb-3 from-tablet:pb-0 w-full">
          <div>
            <Input
              autoComplete="new-password"
              label="ustaw hasło"
              name="password-register"
              onChange={handlePasswordChange}
              suffix={
                <div
                  onClick={() => {
                    setIsPassVisible((v) => {
                      return !v;
                    });
                  }}
                  role="button"
                >
                  <Icon
                    className="cursor-pointer"
                    height={28}
                    name={shouldShowPassword ? "eye" : "eye-slash"}
                    width={28}
                  />
                </div>
              }
              type={shouldShowPassword ? "text" : "password"}
              value={context.password}
            />
            {context.passwordError && (
              <ErrorMessage message={context.passwordError} />
            )}
          </div>
          <PasswordValidation value={context.password} />
        </div>
        <Button
          display={{
            "@onlyMobile": "inline",
            "@fromTablet": "none",
          }}
          isLoading={isLoading}
          testId="submit-mobile"
          type="submit"
        >
          załóż konto
        </Button>
        <Separator
          size={
            offerId
              ? { "@onlyMobile": "xl", "@fromTablet": "m" }
              : { "@onlyMobile": "xl", "@fromTablet": "xs" }
          }
          tone={offerId ? "complementary" : { "@fromTablet": "invisible" }}
        />
        <div className="flex justify-center mb-3">
          <Text size={{ "@onlyMobile": "m", "@fromTablet": "m" }}>
            lub zarejestruj się przez:
          </Text>
        </div>
        <div
          className="flex flex-col mb-4 from-desktop:flex-row gap-2"
          onClick={storeDataInSessionStorage}
        >
          <Link
            className="from-desktop:w-[50%]"
            href={getIdentityProviderLoginUrl(
              "google",
              portalRoutes.zaloguj.index.get(),
              "portal",
            )}
            passHref={true}
          >
            <A className="flex gap-2" variant="google">
              <Icon name="google" />
              <Text>użyj konta Google</Text>
            </A>
          </Link>
          <Link
            className="from-desktop:w-[50%]"
            href={getIdentityProviderLoginUrl(
              "facebook",
              portalRoutes.zaloguj.index.get(),
              "portal",
            )}
            passHref={true}
          >
            <A className="flex gap-2" variant="facebook">
              <Icon name="facebook" />
              <Text tone="secondary">użyj konta Facebook</Text>
            </A>
          </Link>
        </div>
        <Text>
          Masz już konto {`"nju z aplikacją"`}?{" "}
          <Link
            className="underline"
            href={
              offerId
                ? portalRoutes.zaloguj.index.get({
                    mode: mode || "ftth",
                    flow:
                      flow === "multiOrder" &&
                      mode &&
                      ["mobile", "postpaid"].includes(mode)
                        ? "multiOrder"
                        : "ONBOARDING",
                    oferta: offerId,
                    ...(params?.amount &&
                      mode &&
                      ["mobile", "postpaid"].includes(mode) && {
                        amount: params.amount,
                      }),
                  })
                : portalRoutes.zaloguj.index.get()
            }
          >
            Zaloguj się
          </Link>
        </Text>
      </div>
      {context.formError && (
        <div className="my-4">
          <ErrorMessage message={context.formError} withIcon={true} />
        </div>
      )}
      <ButtonsWrapper>
        <Button onPress={router.back} size="m" variant="secondary">
          powrót
        </Button>
        <Button
          display={{
            "@onlyMobile": "none",
            "@fromTablet": "inline",
          }}
          isLoading={isLoading}
          testId="submit"
          type="submit"
        >
          załóż konto
        </Button>
      </ButtonsWrapper>
    </form>
  );
}
